import type { KeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from '../lib/use-scroll-lock.js'

type Props = {
  children: ReactNode
  /** backdrop の Tailwind クラス（例: bg-black/50） */
  className?: string
  /** 背景クリックで閉じる */
  onBackdropClick?: () => void
  /** Escape キーで閉じる */
  onEscape?: () => void
  'aria-label'?: string
  'aria-modal'?: boolean
  role?: string
}

/**
 * 全画面モーダルの共通オーバーレイ。
 * - document.body に portal してビューポート全体を覆う（main 内 fixed の閉じ込めを回避）
 * - 背景スクロールをロック（AppShell の main または documentElement）
 * - オーバーレイ内を overflow-y-auto にして高いモーダルもスクロール可能
 */
export function ModalOverlay({
  children,
  className = 'bg-black/50',
  onBackdropClick,
  onEscape,
  'aria-label': ariaLabel,
  'aria-modal': ariaModal = true,
  role = 'dialog',
}: Props) {
  useScrollLock(true)

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && onEscape) onEscape()
  }

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] overflow-y-auto overscroll-contain p-4 ${className}`}
      onClick={onBackdropClick}
      onKeyDown={handleKeyDown}
      role={role}
      aria-modal={ariaModal}
      aria-label={ariaLabel}
    >
      <div className="flex min-h-full items-center justify-center">{children}</div>
    </div>,
    document.body,
  )
}
